import { PaneNode, TabState } from '../../types/pane.types';
import { TabContainer as TabContainerType, SplitPane as SplitPaneType } from '../../types/pane.types';
import { MIDASProject } from '../../types/project.types';
import { createDatasetId, createPaneId, createTabId } from '../../types';
import { 
  createDataTableTab, 
  createStatisticsTab, 
  createSelectedRowsTab 
} from '../tab-system/TabRegistry';
import logger from './logger';

/**
 * プロジェクト固有の初期レイアウトを作成
 * 
 * プロジェクト読み込み時に、データセット情報を使用して完全なレイアウトを作成する。
 * 左側にデータテーブル、右上にStatistics、右下にSelected Rowsタブを配置した
 * 3分割レイアウト構造を実際のタブとともに生成する。
 * 
 * @param project - 読み込まれたプロジェクトデータ
 * @returns データセット固有のタブを含む完全なレイアウト
 */
export function createInitialLayout(project: MIDASProject): PaneNode {
  const datasetCount = Object.keys(project.datasets || {}).length;
  
  // プロジェクトにデータセットがある場合、分割レイアウトを作成
  if (project.datasets && datasetCount > 0) {
    const firstDatasetId = Object.keys(project.datasets)[0];
    const firstDataset = project.datasets[createDatasetId(firstDatasetId)];
    
    
    // 左側: データテーブルコンテナ（60%）
    const dataTableTab = createDataTableTab(createDatasetId(firstDatasetId), firstDataset.name);
    const leftContainer: TabContainerType = {
      type: 'container',
      id: createPaneId('left-container'),
      activeTabId: dataTableTab.id,
      tabs: [dataTableTab]
    };

    // 右上: 統計タブコンテナ
    const statisticsTab = createStatisticsTab();
    const rightTopContainer: TabContainerType = {
      type: 'container',
      id: createPaneId('right-top-container'),
      activeTabId: statisticsTab.id,
      tabs: [statisticsTab]
    };

    // 右下: Selected Rowsタブコンテナ
    const selectedRowsTab = createSelectedRowsTab(createDatasetId(firstDatasetId));
    const rightBottomContainer: TabContainerType = {
      type: 'container',
      id: createPaneId('right-bottom-container'),
      activeTabId: selectedRowsTab.id,
      tabs: [selectedRowsTab]
    };

    // 右側を垂直分割（Statistics上、Selected Rows下）
    const rightSplit: SplitPaneType = {
      type: 'split',
      id: createPaneId('right-split'),
      direction: 'vertical',
      leftChild: rightTopContainer,
      rightChild: rightBottomContainer,
      splitRatio: 0.75 // 上75%, 下25%
    };

    // 全体を水平分割（左側データテーブル、右側統計エリア）
    const splitPane: SplitPaneType = {
      type: 'split',
      id: createPaneId('root-split'),
      direction: 'horizontal',
      leftChild: leftContainer,
      rightChild: rightSplit,
      splitRatio: 0.6 // 左側60%, 右側40%
    };

    
    return splitPane;
  } else {
    // データセットがない場合は単一コンテナ
    const rootContainer: TabContainerType = {
      type: 'container',
      id: createPaneId('root-container'),
      activeTabId: createTabId(''),
      tabs: []
    };
    return rootContainer;
  }
}

/**
 * 残差分析用の初期レイアウトを作成
 * 
 * @param project - プロジェクトデータ
 * @param datasetId - 残差分析対象のデータセットID
 * @returns 残差分析用のレイアウト
 */
export function createResidualAnalysisLayout(
  project: MIDASProject,
  datasetId: string
): PaneNode {
  
  if (!project.datasets || !project.datasets[createDatasetId(datasetId)]) {
    logger.error('[layoutUtils] 指定されたデータセットが見つかりません:', datasetId);
    // フォールバック: 通常の初期レイアウトを作成
    return createInitialLayout(project);
  }
  
  const dataset = project.datasets[createDatasetId(datasetId)];
  
  // 左側: データテーブルコンテナ
  const dataTableTab = createDataTableTab(createDatasetId(datasetId), dataset.name);
  const leftContainer: TabContainerType = {
    type: 'container',
    id: createPaneId('left-container'),
    activeTabId: dataTableTab.id,
    tabs: [dataTableTab]
  };

  // 右側: 統計タブコンテナ（残差分析モードで開始）
  const statisticsTab = createStatisticsTab();
  const rightContainer: TabContainerType = {
    type: 'container',
    id: createPaneId('right-container'),
    activeTabId: statisticsTab.id,
    tabs: [statisticsTab]
  };

  // 全体を水平分割
  const splitPane: SplitPaneType = {
    type: 'split',
    id: createPaneId('root-split'),
    direction: 'horizontal',
    leftChild: leftContainer,
    rightChild: rightContainer,
    splitRatio: 0.5 // 左右均等
  };

  
  return splitPane;
}

/**
 * アクティブタブを復元するヘルパー関数
 * 
 * @param rootPane - ルートペインノード
 * @param activeTabId - アクティブにするタブのID
 * @param activateTab - タブをアクティブにする関数
 */
export function restoreActiveTab(
  rootPane: PaneNode,
  activeTabId: string | null | undefined,
  activateTab: (containerId: string, tabId: string) => void
): void {
  if (!activeTabId || !rootPane) {
    // デフォルトの最初のタブをアクティブにする
    const findFirstTab = (node: PaneNode): { containerId: string; tabId: string } | null => {
      if (node.type === 'container' && node.tabs.length > 0) {
        return { containerId: node.id, tabId: node.tabs[0].id };
      } else if (node.type === 'split') {
        return findFirstTab(node.leftChild) || findFirstTab(node.rightChild);
      }
      return null;
    };
    
    const firstTab = findFirstTab(rootPane);
    if (firstTab) {
      activateTab(firstTab.containerId, firstTab.tabId);
    }
    return;
  }

  // レイアウト内でタブIDを検索してアクティブ化
  const findAndActivateTab = (node: PaneNode, tabId: string): boolean => {
    if (node.type === 'container') {
      const tab = node.tabs.find((t: TabState) => t.id === tabId);
      if (tab) {
        activateTab(node.id, tabId);
        return true;
      }
      return false;
    } else {
      return findAndActivateTab(node.leftChild, tabId) || findAndActivateTab(node.rightChild, tabId);
    }
  };

  const found = findAndActivateTab(rootPane, activeTabId);
  if (!found) {
    logger.warn('[layoutUtils] 保存されたアクティブタブが見つかりません:', activeTabId);
    // フォールバック: デフォルトの最初のタブをアクティブにする
    const findFirstTab = (node: PaneNode): { containerId: string; tabId: string } | null => {
      if (node.type === 'container' && node.tabs.length > 0) {
        return { containerId: node.id, tabId: node.tabs[0].id };
      } else if (node.type === 'split') {
        return findFirstTab(node.leftChild) || findFirstTab(node.rightChild);
      }
      return null;
    };
    
    const firstTab = findFirstTab(rootPane);
    if (firstTab) {
      activateTab(firstTab.containerId, firstTab.tabId);
    }
  }
}

/**
 * レイアウトが有効かどうかをチェック
 * 
 * @param layout - チェックするレイアウト
 * @returns レイアウトが有効な場合はtrue
 */
export function validateLayout(layout: PaneNode | null): boolean {
  if (!layout) return false;
  
  const hasValidStructure = (node: PaneNode): boolean => {
    if (node.type === 'container') {
      // コンテナは少なくとも1つのタブを持つべき
      return node.tabs.length > 0;
    } else if (node.type === 'split') {
      // 分割ペインは両方の子が有効である必要がある
      return hasValidStructure(node.leftChild) && hasValidStructure(node.rightChild);
    }
    return false;
  };
  
  return hasValidStructure(layout);
}

/**
 * レイアウト内のすべてのタブを取得
 * 
 * @param node - ペインノード
 * @returns すべてのタブの配列
 */
export function getAllTabs(node: PaneNode): TabState[] {
  if (node.type === 'container') {
    return node.tabs;
  } else {
    return [...getAllTabs(node.leftChild), ...getAllTabs(node.rightChild)];
  }
}

/**
 * レイアウト内のすべてのコンテナIDを取得
 *
 * @param node - ペインノード
 * @returns すべてのコンテナIDの配列
 */
export function getAllContainerIds(node: PaneNode): string[] {
  if (node.type === 'container') {
    return [node.id];
  } else {
    return [...getAllContainerIds(node.leftChild), ...getAllContainerIds(node.rightChild)];
  }
}

/**
 * レイアウト内の最初のコンテナIDを取得
 *
 * @param node - ペインノード
 * @returns 最初のコンテナID、見つからない場合はnull
 */
export function getFirstContainerId(node: PaneNode): string | null {
  if (node.type === 'container') {
    return node.id;
  } else {
    // 分割ペインの場合、左側（またはleftChild）を優先
    return getFirstContainerId(node.leftChild) || getFirstContainerId(node.rightChild);
  }
}